import { Crop, Download, ExternalLink, File as FileIcon, FileText, Image as ImageIcon, Link2, Music, Plus, Search, Trash2, UserRound, Video, X } from 'lucide-react';
import { useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { putFiles } from '../../model/db';
import { fileKey, mediaKindOf, mimeFromName, type FileBag } from '../../model/files';
import { displayName, newId, shortName } from '../../model/people';
import { sourceLabel } from '../../model/research';
import { removeMedia, upsertMedia, upsertPerson } from '../../model/tree';
import type { Id, MediaItem, MediaKind, Region } from '../../model/types';
import { Empty, Field, Modal, PersonRow } from '../components/common';
import { DateInput } from '../components/DateInput';
import { PersonPicker } from '../components/PersonPicker';
import { fileUrl, useFileUrl } from '../files';
import { MEDIA_KIND_LABELS } from '../labels';
import { photoFromBlob, thumbFromBlob } from '../photo';
import { closeDialog, commit, confirmAction, getState, openDialog, showPerson, toast, useApp } from '../store';
import { formatDate } from '../../model/dates';

const KIND_ICONS: Record<MediaKind, typeof ImageIcon> = { image: ImageIcon, pdf: FileText, audio: Music, video: Video, link: Link2, file: FileIcon };

/** Store picked or dropped files as media, tagged with `personIds`. Returns the new items' ids. */
export async function addMediaFiles(list: File[], personIds: Id[] = []): Promise<Id[]> {
  if (!list.length) return [];
  const files: FileBag = new Map();
  const items: MediaItem[] = [];
  for (const f of list) {
    const bytes = new Uint8Array(await f.arrayBuffer());
    const key = await fileKey(bytes);
    const type = f.type || mimeFromName(f.name);
    const blob = new Blob([bytes], { type });
    files.set(key, blob);
    const kind = mediaKindOf(type, f.name);
    const item: MediaItem = {
      id: newId('m'),
      title: f.name.replace(/\.[^.]+$/, ''),
      kind,
      file: { key, type, size: bytes.length, name: f.name },
      people: personIds.map((personId) => ({ personId })),
    };
    if (kind === 'image') {
      try {
        item.thumb = await thumbFromBlob(blob);
      } catch {
        /* shown with an icon */
      }
    }
    items.push(item);
  }
  await putFiles(files);
  let t = getState().tree!;
  for (const it of items) t = upsertMedia(t, it);
  commit(t);
  toast(items.length === 1 ? `Added “${items[0].title}”.` : `Added ${items.length} media items.`, 'success');
  return items.map((i) => i.id);
}

/** A file input that hands its files to addMediaFiles. Click it with `ref.current.click()`. */
export function MediaFileInput({ inputRef, personIds, onAdded }: { inputRef: React.RefObject<HTMLInputElement>; personIds?: Id[]; onAdded?: (ids: Id[]) => void }) {
  return (
    <input
      ref={inputRef}
      type="file"
      multiple
      hidden
      onChange={async (e) => {
        const list = [...(e.target.files ?? [])];
        e.target.value = '';
        try {
          onAdded?.(await addMediaFiles(list, personIds));
        } catch (err) {
          toast(`Couldn’t add that file: ${(err as Error).message}`, 'error');
        }
      }}
    />
  );
}

export function MediaThumb({ item, size = 'md' }: { item: MediaItem; size?: 'sm' | 'md' }) {
  const Icon = KIND_ICONS[item.kind];
  return (
    <span className={`media-thumb ${size}`} aria-hidden>
      {item.thumb ? <img src={item.thumb} alt="" /> : <Icon size={size === 'sm' ? 18 : 28} />}
    </span>
  );
}

export function MediaView() {
  const tree = useApp((s) => s.tree)!;
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<MediaKind | ''>('');
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const items = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return Object.values(tree.media)
      .filter((m) => !kind || m.kind === kind)
      .filter((m) => {
        if (!needle) return true;
        const names = m.people.map((x) => (tree.people[x.personId] ? displayName(tree.people[x.personId]) : '')).join(' ');
        return `${m.title} ${m.description ?? ''} ${names}`.toLowerCase().includes(needle);
      })
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [tree, q, kind]);
  const total = Object.keys(tree.media).length;

  return (
    <div
      className={`page${over ? ' drop-over' : ''}`}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        e.stopPropagation();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        // Files dropped here are media, not a tree to import (the window-wide handler).
        e.preventDefault();
        e.stopPropagation();
        setOver(false);
        addMediaFiles([...e.dataTransfer.files]).catch((err) => toast((err as Error).message, 'error'));
      }}
    >
      <div className="page-inner" style={{ maxWidth: 1100 }}>
        <div className="page-head">
          <div>
            <h1 style={{ fontSize: 22 }}>Media</h1>
            <p className="muted" style={{ marginTop: 4 }}>Photos, documents, recordings, and links.<span className="for-mouse"> Drop files here to add them.</span></p>
          </div>
          <div className="row">
            <button
              className="btn"
              onClick={() => {
                // A draft until saved, so Cancel leaves nothing behind.
                const item: MediaItem = { id: newId('m'), title: '', kind: 'link', url: '', people: [] };
                openDialog({ type: 'media-details', mediaId: item.id, draft: item });
              }}
            >
              <Link2 size={15} /> Add a link
            </button>
            <button className="btn primary" onClick={() => input.current?.click()}>
              <Plus size={15} /> Add files
            </button>
            <MediaFileInput inputRef={input} />
          </div>
        </div>
        {total > 0 && (
          <div className="row wrap">
            <label className="search-box" style={{ flex: '1 1 240px' }}>
              <Search size={15} />
              <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles, descriptions, and people" aria-label="Search media" />
            </label>
            <select className="select" style={{ width: 'auto' }} value={kind} onChange={(e) => setKind(e.target.value as MediaKind | '')} aria-label="Filter by kind">
              <option value="">All kinds</option>
              {Object.entries(MEDIA_KIND_LABELS).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
          </div>
        )}
        {total === 0 ? (
          <div className="card-box">
            <Empty icon={<ImageIcon size={28} />} title="No media yet">
              Add photos, scanned documents, recordings, and links, then tag the people in them.
            </Empty>
          </div>
        ) : items.length === 0 ? (
          <p className="muted">Nothing matches.</p>
        ) : (
          <div className="media-grid">
            {items.map((m) => (
              <button key={m.id} className="media-tile" aria-label={m.title} onClick={() => openDialog({ type: 'media-details', mediaId: m.id })}>
                <MediaThumb item={m} />
                <span className="title">{m.title}</span>
                <span className="meta">
                  {[MEDIA_KIND_LABELS[m.kind], formatDate(m.date), m.people.length ? `${m.people.length} ${m.people.length === 1 ? 'person' : 'people'}` : '', !m.file && m.kind !== 'link' ? 'File not added' : '']
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function fileSize(n: number): string {
  if (n < 1024) return `${n} bytes`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** "example.com" from a web address, to name a link saved without a title. */
function hostOf(url: string | undefined): string {
  try {
    return url ? new URL(url).hostname.replace(/^www\./, '') : '';
  } catch {
    return '';
  }
}

/** One media item: see it, and edit its details, people, and sources. */
export function MediaDetails({ mediaId, draft }: { mediaId: Id; draft?: MediaItem }) {
  const tree = useApp((s) => s.tree)!;
  const original = tree.media[mediaId];
  const [m, setM] = useState<MediaItem | undefined>(() => (original ? structuredClone(original) : draft ? structuredClone(draft) : undefined));
  const isNew = !original && !!draft;
  // Who we're marking in the picture, and the rectangle being drawn.
  const [marking, setMarking] = useState<Id | null>(null);
  const replaceInput = useRef<HTMLInputElement>(null);
  const { url, missing } = useFileUrl(m?.file?.key);
  if (!m) {
    return (
      <Modal title="Media not found" footer={<button className="btn" onClick={closeDialog}>Close</button>}>
        <p className="muted">This item has been deleted.</p>
      </Modal>
    );
  }
  const set = (patch: Partial<MediaItem>) => setM((x) => (x ? { ...x, ...patch } : x));
  const save = () => {
    commit(upsertMedia(getState().tree!, { ...m, title: m.title.trim() || hostOf(m.url) || 'Untitled' }));
    closeDialog();
  };
  const setRegion = (personId: Id, region: Region | undefined) => set({ people: m.people.map((x) => (x.personId === personId ? (region ? { ...x, region } : { personId }) : x)) });

  const useAsPhoto = async (personId: Id) => {
    if (!m.file) return;
    try {
      const blob = await fetch((await fileUrl(m.file.key))!).then((r) => r.blob());
      const photo = await photoFromBlob(blob, 256, m.people.find((x) => x.personId === personId)?.region);
      const p = getState().tree!.people[personId];
      commit(upsertPerson(getState().tree!, { ...p, photo }));
      toast(`Updated ${shortName(p)}’s photo.`, 'success');
    } catch {
      toast('Couldn’t read that picture.', 'error');
    }
  };

  const replaceFile = async (f: File) => {
    const bytes = new Uint8Array(await f.arrayBuffer());
    const key = await fileKey(bytes);
    const type = f.type || mimeFromName(f.name);
    const blob = new Blob([bytes], { type });
    await putFiles(new Map([[key, blob]]));
    // The new file decides the kind and the thumbnail: a photo replaced by a PDF is a PDF now.
    const kind = mediaKindOf(type, f.name);
    const thumb = kind === 'image' ? await thumbFromBlob(blob).catch(() => undefined) : undefined;
    set({ file: { key, type, size: bytes.length, name: f.name }, kind, thumb });
  };

  const people = m.people.filter((x) => tree.people[x.personId]);
  const sourceOptions = Object.values(tree.sources).filter((s) => !m.sourceIds?.includes(s.id)).sort((a, b) => a.title.localeCompare(b.title));

  return (
    <Modal
      title={isNew ? 'New link' : (original ?? m).title || 'Media'}
      wide
      className="media-modal"
      footer={
        <>
          {!isNew && (
            <button
              className="btn danger left"
              onClick={() =>
                confirmAction({
                  title: `Delete “${(original ?? m).title}”?`,
                  body: 'The item and its file are removed from this tree. The people in it are not affected.',
                  confirmLabel: 'Delete',
                  danger: true,
                  onConfirm: () => {
                    commit(removeMedia(getState().tree!, mediaId));
                    closeDialog();
                  },
                })
              }
            >
              <Trash2 size={15} /> Delete
            </button>
          )}
          <button className="btn" onClick={closeDialog}>
            Cancel
          </button>
          <button className="btn primary" onClick={save}>
            Save
          </button>
        </>
      }
    >
      <div className="media-layout">
        <div className="media-preview">
          {m.kind === 'image' && url ? (
            <RegionImage src={url} item={m} marking={marking} onMark={(region) => {
              if (marking) setRegion(marking, region);
              setMarking(null);
            }} />
          ) : m.kind === 'pdf' && url ? (
            <iframe src={url} title={m.title} />
          ) : m.kind === 'audio' && url ? (
            <audio src={url} controls />
          ) : m.kind === 'video' && url ? (
            <video src={url} controls />
          ) : m.kind === 'link' ? (
            <div className="media-placeholder">
              <Link2 size={32} />
              {m.url ? (
                <a className="btn" href={m.url} target="_blank" rel="noreferrer noopener">
                  <ExternalLink size={15} /> Open link
                </a>
              ) : (
                <span className="muted small-text">Add its web address to open it from here.</span>
              )}
            </div>
          ) : (
            <div className="media-placeholder">
              <MediaThumb item={m} />
              {m.file && !missing ? (
                url && (
                  <a className="btn" href={url} download={m.file.name ?? m.title}>
                    <Download size={15} /> Download
                  </a>
                )
              ) : (
                <>
                  <span className="muted small-text" style={{ textAlign: 'center' }}>
                    {m.file ? 'This file is missing from this browser’s storage.' : 'The file itself isn’t in this tree.'}
                    {m.url ? <><br />It was at: <code>{m.url}</code></> : null}
                  </span>
                  <button className="btn" onClick={() => replaceInput.current?.click()}>
                    <Plus size={15} /> Add the file
                  </button>
                </>
              )}
            </div>
          )}
          {marking && <div className="callout small-text">Drag a box around {shortName(tree.people[marking])} in the picture. <button className="link-btn" onClick={() => setMarking(null)}>Cancel</button></div>}
          {m.file && url && (
            <div className="row small-text muted" style={{ justifyContent: 'space-between' }}>
              <span>{[m.file.name, fileSize(m.file.size)].filter(Boolean).join(' · ')}</span>
              <span className="row">
                <a className="link-btn" href={url} download={m.file.name ?? m.title}>
                  Download
                </a>
                <button className="link-btn" onClick={() => replaceInput.current?.click()}>
                  Replace file
                </button>
              </span>
            </div>
          )}
          <input
            ref={replaceInput}
            type="file"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) replaceFile(f).catch(() => toast('Couldn’t read that file.', 'error'));
            }}
          />
        </div>
        <div className="stack">
          <Field label="Title">
            <input className="input" value={m.title} onChange={(e) => set({ title: e.target.value })} />
          </Field>
          {m.kind === 'link' && (
            <Field label="Web address">
              <input className="input" type="url" value={m.url ?? ''} placeholder="https://" onChange={(e) => set({ url: e.target.value || undefined })} />
            </Field>
          )}
          <Field label="Date">
            <DateInput value={m.date} onChange={(date) => set({ date })} placeholder="When it was taken or made" />
          </Field>
          <Field label="Description">
            <textarea className="textarea" style={{ minHeight: 60 }} value={m.description ?? ''} onChange={(e) => set({ description: e.target.value || undefined })} />
          </Field>
          <div className="section-title">People ({people.length})</div>
          <PersonPicker
            tree={tree}
            value={null}
            exclude={m.people.map((x) => x.personId)}
            placeholder="Tag someone: search by name…"
            ariaLabel="Tag someone in this item"
            onChange={(id) => id && set({ people: [...m.people, { personId: id }] })}
          />
          <div className="stack tight">
            {people.map((x) => (
              <PersonRow
                key={x.personId}
                person={tree.people[x.personId]}
                meta={x.region ? 'Marked in the picture' : undefined}
                onClick={() => showPerson(x.personId)}
                actions={
                  <>
                    {m.kind === 'image' && url && (
                      <button className="btn icon small ghost" title={x.region ? 'Mark them again' : 'Mark where they are'} aria-label={`Mark where ${displayName(tree.people[x.personId])} is`} onClick={() => setMarking(x.personId)}>
                        <Crop size={14} />
                      </button>
                    )}
                    {m.kind === 'image' && m.file && (
                      <button className="btn icon small ghost" title="Use as their profile photo" aria-label={`Use as ${displayName(tree.people[x.personId])}'s profile photo`} onClick={() => useAsPhoto(x.personId)}>
                        <UserRound size={14} />
                      </button>
                    )}
                    <button className="btn icon small ghost danger" aria-label="Untag" title="Untag" onClick={() => set({ people: m.people.filter((y) => y.personId !== x.personId) })}>
                      <X size={14} />
                    </button>
                  </>
                }
              />
            ))}
          </div>
          <div className="section-title">Sources</div>
          {(m.sourceIds ?? []).filter((id) => tree.sources[id]).map((id) => (
            <div key={id} className="row small-text">
              <button className="link-btn grow" style={{ textAlign: 'left' }} onClick={() => openDialog({ type: 'edit-source', sourceId: id })}>
                {sourceLabel(tree.sources[id])}
              </button>
              <button className="btn icon small ghost danger" aria-label="Unlink source" onClick={() => set({ sourceIds: m.sourceIds!.filter((x) => x !== id) })}>
                <X size={14} />
              </button>
            </div>
          ))}
          {sourceOptions.length > 0 ? (
            <select className="select" value="" aria-label="Link a source" onChange={(e) => e.target.value && set({ sourceIds: [...(m.sourceIds ?? []), e.target.value] })}>
              <option value="">Link to a source this is a copy of…</option>
              {sourceOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {sourceLabel(s)}
                </option>
              ))}
            </select>
          ) : (
            !m.sourceIds?.length && <p className="faint small-text">No sources in this tree yet.</p>
          )}
        </div>
      </div>
    </Modal>
  );
}

/** A picture with the tagged people's rectangles on it; drag to mark someone when `marking`. */
function RegionImage({ src, item, marking, onMark }: { src: string; item: MediaItem; marking: Id | null; onMark: (r: Region | undefined) => void }) {
  const tree = useApp((s) => s.tree)!;
  const box = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const at = (e: ReactPointerEvent) => {
    const r = box.current!.getBoundingClientRect();
    return { x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) };
  };
  const rect = drag && { x: Math.min(drag.x0, drag.x1), y: Math.min(drag.y0, drag.y1), w: Math.abs(drag.x1 - drag.x0), h: Math.abs(drag.y1 - drag.y0) };
  return (
    <div
      ref={box}
      className={`region-image${marking ? ' marking' : ''}`}
      onPointerDown={(e) => {
        if (!marking) return;
        e.preventDefault();
        (e.target as Element).setPointerCapture?.(e.pointerId);
        const p = at(e);
        setDrag({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
      }}
      onPointerMove={(e) => {
        if (!drag) return;
        const p = at(e);
        setDrag({ ...drag, x1: p.x, y1: p.y });
      }}
      onPointerUp={() => {
        if (!drag || !rect) return;
        setDrag(null);
        onMark(rect.w > 0.01 && rect.h > 0.01 ? rect : undefined);
      }}
    >
      <img src={src} alt={item.title} draggable={false} />
      {!marking &&
        item.people
          .filter((x) => x.region && tree.people[x.personId])
          .map((x) => (
            <span key={x.personId} className="region" style={{ left: `${x.region!.x * 100}%`, top: `${x.region!.y * 100}%`, width: `${x.region!.w * 100}%`, height: `${x.region!.h * 100}%` }}>
              <span className="region-label">{shortName(tree.people[x.personId])}</span>
            </span>
          ))}
      {rect && <span className="region drawing" style={{ left: `${rect.x * 100}%`, top: `${rect.y * 100}%`, width: `${rect.w * 100}%`, height: `${rect.h * 100}%` }} />}
    </div>
  );
}
